import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Visibility as VisibilityIcon, People as PeopleIcon } from '@mui/icons-material'

import type { Contact } from '../../services/api'
import type { ClientPursuit, JobOpportunity } from '../../state/jobOpportunities'
import {
  CLIENT_PURSUIT_COLORS,
  CLIENT_PURSUIT_LABELS,
  SIGNAL_TYPE_LABELS,
  canAnswerPursuit,
} from '../../state/jobOpportunities'
import { contactsForJob, contactDisplayName } from './WarmContactsCell'
import { formatLocalYMD } from '../../utils/dateFormat'

interface HiddenOpportunitiesTabProps {
  jobs: JobOpportunity[]
  // The client's contacts, so each card can name who could open the door. The
  // host has already loaded them; an absent list renders no contacts rather
  // than fetching on the host's behalf, matching JobDetailDialog.
  contacts?: Contact[]
  onOpen: (job: JobOpportunity) => void
  // Staff and the portal reach the same empty state from different places, so
  // the next step differs.
  emptyHint: string
  // MWA-355 — who is looking. The card body is identical for both (the point
  // of the feature is that the client sees what staff saw); this decides who
  // gets the pursue/decline buttons and who is shown the answer as a record.
  audience: 'staff' | 'client'
  // Supplied by the portal only. Absent on the staff page, which reads the
  // answer rather than giving one.
  onPursuit?: (job: JobOpportunity, pursuit: ClientPursuit) => void
  // Id of the opportunity whose answer is currently in flight, so its buttons
  // can disable rather than queue a second write.
  savingPursuitId?: string | null
  // MWA-338 — the staff all-clients board mixes clients, so each card has to
  // say whose it is. Off everywhere a single client is already implied.
  showClientName?: boolean
}

/**
 * MWA-304 — roles we believe are opening, logged from a public signal before
 * any posting exists.
 *
 * One component for both audiences. The card body is identical for each: the
 * whole point of the feature is that the client sees what staff saw, so the
 * client is never rendered a thinner card.
 *
 * MWA-355 added the one thing that does differ. The client answers whether
 * they will chase it; staff read that answer back. Everything else the client
 * still cannot touch, enforced by the API rather than by withheld controls.
 */
export default function HiddenOpportunitiesTab({
  jobs,
  contacts,
  onOpen,
  emptyHint,
  audience,
  onPursuit,
  savingPursuitId,
  showClientName = false,
}: HiddenOpportunitiesTabProps) {
  if (jobs.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <VisibilityIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {emptyHint}
        </Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={1.5} sx={{ p: 2 }}>
      {jobs.map((job) => {
        const jobContacts = contactsForJob(contacts || [], job.company, job.linkedContactIds)
        return (
          <Card key={job.id} variant="outlined">
            <CardActionArea onClick={() => onOpen(job)} sx={{ p: 2 }}>
              {showClientName && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  {job.clientName || '—'}
                </Typography>
              )}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ alignItems: { sm: 'baseline' }, justifyContent: 'space-between' }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {job.jobTitle} — {job.company}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
                  {job.signalType && (
                    <Chip
                      size="small"
                      label={SIGNAL_TYPE_LABELS[job.signalType] || job.signalType}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {jobContacts.length > 0 && (
                    <Tooltip title={jobContacts.map(contactDisplayName).join(', ')}>
                      <Chip
                        size="small"
                        icon={<PeopleIcon />}
                        label={jobContacts.length}
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </Stack>
              </Stack>

              {job.signalText && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {job.signalText}
                </Typography>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Provenance. Laid out as label/value pairs rather than a
                  sentence so a card with only some of them still reads evenly. */}
              <Stack
                direction="row"
                spacing={3}
                sx={{ flexWrap: 'wrap', rowGap: 1, color: 'text.secondary' }}
              >
                {job.vacatedBy && (
                  <Typography variant="caption">
                    Seat vacated by <strong>{job.vacatedBy}</strong>
                  </Typography>
                )}
                {job.signalObservedDate && (
                  <Typography variant="caption">
                    Observed {formatLocalYMD(job.signalObservedDate)}
                  </Typography>
                )}
                {job.signalSource && <Typography variant="caption">Source: {job.signalSource}</Typography>}
              </Stack>
            </CardActionArea>

            {/* MWA-355 — the client's answer, and the controls for giving it.
                Outside the CardActionArea deliberately: a button nested inside
                one fires the card's onClick too, so answering would also open
                the detail dialog behind the toast. */}
            <Box sx={{ px: 2, pb: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
              >
                {job.clientPursuit ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
                  >
                    <Chip
                      size="small"
                      label={CLIENT_PURSUIT_LABELS[job.clientPursuit] || job.clientPursuit}
                      sx={{
                        backgroundColor: CLIENT_PURSUIT_COLORS[job.clientPursuit],
                        color: 'white',
                        fontWeight: 500,
                      }}
                    />
                    {job.clientPursuitAt && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(job.clientPursuitAt).toLocaleDateString()}
                        {/* Staff need to know whose answer it is; the client
                            already knows it is theirs. */}
                        {audience === 'staff' && job.clientPursuitBy
                          ? ` · ${job.clientPursuitBy.split('@')[0]}`
                          : ''}
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  // Unanswered has to read as unanswered on both sides. Left
                  // blank it reads as a decline that nobody recorded.
                  <Typography variant="caption" color="text.secondary">
                    {audience === 'staff'
                      ? 'Awaiting the client’s decision'
                      : 'Tell us whether this one is worth chasing.'}
                  </Typography>
                )}

                {audience === 'client' && onPursuit && canAnswerPursuit(job) && (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
                    {savingPursuitId === job.id && <CircularProgress size={16} />}
                    <Button
                      size="small"
                      variant={job.clientPursuit === 'pursuing' ? 'contained' : 'outlined'}
                      color="success"
                      disabled={savingPursuitId === job.id}
                      onClick={() => onPursuit(job, 'pursuing')}
                    >
                      {CLIENT_PURSUIT_LABELS.pursuing}
                    </Button>
                    <Button
                      size="small"
                      variant={job.clientPursuit === 'declined' ? 'contained' : 'outlined'}
                      color="inherit"
                      disabled={savingPursuitId === job.id}
                      onClick={() => onPursuit(job, 'declined')}
                    >
                      {CLIENT_PURSUIT_LABELS.declined}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Card>
        )
      })}
    </Stack>
  )
}
